import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Trash2, Eye, EyeOff, Lock, AlertTriangle, Edit2, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import SecurityVerification, { useSecurityStatus, isSecurityVerified } from '../components/SecurityVerification';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CredentialsVault() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [showWarningBanner, setShowWarningBanner] = useState(true);
  const [newCredential, setNewCredential] = useState({
    platform: '',
    username: '',
    password: '',
    notes: ''
  });

  // Security verification state
  const [securityVerificationOpen, setSecurityVerificationOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  // Auto-hide warning banner after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWarningBanner(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  const { hasPin, loading: securityLoading } = useSecurityStatus();

  // Check if PIN verification is needed
  useEffect(() => {
    if (!securityLoading && hasPin && !isSecurityVerified()) {
      setSecurityVerificationOpen(true);
    } else if (!hasPin || isSecurityVerified()) {
      setIsVerified(true);
    }
  }, [securityLoading, hasPin]);

  useEffect(() => {
    if (isVerified) {
      loadCredentials();
    }
  }, [isVerified]);

  const loadCredentials = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredentials(response.data);
    } catch (error) {
      console.error('Failed to load credentials:', error);
      toast.error('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const createCredential = async () => {
    if (!newCredential.platform || !newCredential.password) {
      toast.error('Platform and password are required');
      return;
    }

    const token = localStorage.getItem('session_token');
    setIsCreating(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/credentials`,
        newCredential,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Credential saved securely');
      setNewCredential({ platform: '', username: '', password: '', notes: '' });
      setCreateDialogOpen(false);
      loadCredentials();
    } catch (error) {
      console.error('Failed to save credential:', error);
      toast.error('Failed to save credential');
    } finally {
      setIsCreating(false);
    }
  };

  const updateCredential = async () => {
    if (!editingCredential) return;
    
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(
        `${BACKEND_URL}/api/credentials/${editingCredential.credential_id}`,
        {
          platform: editingCredential.platform,
          username: editingCredential.username,
          password: editingCredential.password,
          notes: editingCredential.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Credential updated');
      setEditDialogOpen(false);
      setEditingCredential(null);
      loadCredentials();
    } catch (error) {
      console.error('Failed to update credential:', error);
      toast.error('Failed to update credential');
    }
  };

  const deleteCredential = async (credentialId) => {
    const token = localStorage.getItem('session_token');

    try {
      await axios.delete(`${BACKEND_URL}/api/credentials/${credentialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Credential deleted');
      setSelectedCredential(null);
      loadCredentials();
    } catch (error) {
      console.error('Failed to delete credential:', error);
      toast.error('Failed to delete credential');
    }
  };

  const revealPassword = async (credentialId) => {
    const token = localStorage.getItem('session_token');

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/credentials/${credentialId}/reveal`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRevealedPasswords({
        ...revealedPasswords,
        [credentialId]: response.data.password
      });
    } catch (error) {
      console.error('Failed to reveal password:', error);
      toast.error('Failed to reveal password');
    }
  };

  const hidePassword = (credentialId) => {
    const updated = { ...revealedPasswords };
    delete updated[credentialId];
    setRevealedPasswords(updated);
  };

  // Show security verification if not verified
  if (!isVerified && hasPin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5] flex items-center justify-center">
        <SecurityVerification
          open={securityVerificationOpen}
          onClose={() => {
            setSecurityVerificationOpen(false);
            navigate('/dashboard');
            toast.error('Security verification required to access Credentials Vault');
          }}
          onVerified={() => {
            setIsVerified(true);
            setSecurityVerificationOpen(false);
          }}
          title="Unlock Credentials Vault"
          description="Enter your security PIN to access your stored credentials"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#2B4C7E] border-r-transparent mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]" data-testid="credentials-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBF0F5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="rounded-full flex-shrink-0">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2 min-w-0">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-[#2B4C7E] flex-shrink-0" />
                <h1 className="text-lg sm:text-2xl font-medium bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] bg-clip-text text-transparent truncate">Credentials Vault</h1>
              </div>
            </div>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full hover:from-[#1E3A63] hover:to-[#1E3A63] flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" 
              data-testid="add-credential-btn"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
              <span className="hidden sm:inline">Add Credential</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Warning Banner - Auto-hides after 3 seconds */}
      <AnimatePresence>
        {showWarningBanner && (
          <motion.div 
            className="max-w-7xl mx-auto px-4 md:px-8 pt-4"
            initial={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="p-4 bg-[#F7F1E8] border-[#EAD9BF]">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-[#B3956B] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-[#836A45]">
                  <strong>Security Notice:</strong> These credentials are encrypted and will only be accessible to your designated primary beneficiary after the Easy Passing process is triggered.
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 md:p-8">
        {credentials.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 bg-[#EBF0F5] rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="h-10 w-10 text-[#3A5C8F]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-medium text-gray-800 mb-3">No credentials stored yet</h3>
            <p className="text-gray-600 mb-6">Securely store passwords and account information for your primary beneficiary</p>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Your First Credential
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {credentials.map((credential) => {
              const isSelected = selectedCredential === credential.credential_id;
              
              return (
                <motion.div
                  key={credential.credential_id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card 
                    className={`p-6 rounded-2xl border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-[#2B4C7E] shadow-xl shadow-[#EBF0F5]' 
                        : 'border-[#EBF0F5] hover:border-[#B9C7D6] hover:shadow-lg'
                    }`}
                    onClick={() => setSelectedCredential(isSelected ? null : credential.credential_id)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isSelected ? 'bg-[#2B4C7E]' : 'bg-[#EBF0F5]'
                        }`}>
                          <Lock className={`h-6 w-6 ${isSelected ? 'text-white' : 'text-[#2B4C7E]'}`} />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">{credential.platform}</h3>
                          <p className="text-sm text-gray-500">Click to reveal details</p>
                        </div>
                      </div>
                    </div>

                    {/* Shaded/Blurred content until selected */}
                    <div className={`space-y-3 transition-all duration-300 ${
                      isSelected ? 'opacity-100' : 'opacity-30 blur-sm pointer-events-none'
                    }`}>
                      {credential.username && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Username</p>
                          <p className="text-sm font-medium text-gray-800">{credential.username}</p>
                        </div>
                      )}
                      
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Password</p>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-mono text-gray-800">
                            {revealedPasswords[credential.credential_id] || '••••••••••'}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (revealedPasswords[credential.credential_id]) {
                                hidePassword(credential.credential_id);
                              } else {
                                revealPassword(credential.credential_id);
                              }
                            }}
                            className="h-8 w-8 p-0"
                          >
                            {revealedPasswords[credential.credential_id] ? (
                              <EyeOff className="h-4 w-4 text-gray-500" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-500" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {credential.notes && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Notes</p>
                          <p className="text-sm text-gray-700">{credential.notes}</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingCredential(credential);
                            setEditDialogOpen(true);
                          }}
                          className="flex-1 rounded-full"
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm('Are you sure you want to delete this credential?')) {
                              deleteCredential(credential.credential_id);
                            }
                          }}
                          className="flex-1 rounded-full text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      {/* Create Credential Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md bg-white rounded-3xl">
          <DialogHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#EBF0F5] to-[#EBF0F5] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-[#2B4C7E]" />
            </div>
            <DialogTitle className="text-2xl font-bold text-gray-900">Add New Credential</DialogTitle>
            <p className="text-gray-600 mt-2">Store login information securely</p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Platform/Website *</label>
              <Input
                placeholder="e.g., Gmail, Facebook, Bank of America"
                value={newCredential.platform}
                onChange={(e) => setNewCredential({ ...newCredential, platform: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Username/Email</label>
              <Input
                placeholder="Enter username or email"
                value={newCredential.username}
                onChange={(e) => setNewCredential({ ...newCredential, username: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
              <Input
                type="password"
                placeholder="Enter password"
                value={newCredential.password}
                onChange={(e) => setNewCredential({ ...newCredential, password: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
              <Textarea
                placeholder="Security questions, recovery info, etc."
                value={newCredential.notes}
                onChange={(e) => setNewCredential({ ...newCredential, notes: e.target.value })}
                rows={3}
                className="rounded-xl"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button 
              onClick={createCredential} 
              disabled={isCreating || !newCredential.platform || !newCredential.password}
              className="w-full bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full hover:from-[#1E3A63] hover:to-[#1E3A63] h-12"
            >
              {isCreating ? 'Saving...' : 'Save Credential'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Credential Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md bg-white rounded-3xl">
          <DialogHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#EBF0F5] to-[#EBF0F5] rounded-full flex items-center justify-center mx-auto mb-4">
              <Edit2 className="h-8 w-8 text-[#2B4C7E]" />
            </div>
            <DialogTitle className="text-2xl font-bold text-gray-900">Edit Credential</DialogTitle>
          </DialogHeader>
          {editingCredential && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Platform/Website</label>
                <Input
                  value={editingCredential.platform}
                  onChange={(e) => setEditingCredential({ ...editingCredential, platform: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Username/Email</label>
                <Input
                  value={editingCredential.username || ''}
                  onChange={(e) => setEditingCredential({ ...editingCredential, username: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <Input
                  type="password"
                  placeholder="Enter new password (leave blank to keep current)"
                  value={editingCredential.password || ''}
                  onChange={(e) => setEditingCredential({ ...editingCredential, password: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <Textarea
                  value={editingCredential.notes || ''}
                  onChange={(e) => setEditingCredential({ ...editingCredential, notes: e.target.value })}
                  rows={3}
                  className="rounded-xl"
                />
              </div>
            </div>
          )}
          <DialogFooter className="mt-6 gap-3">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1 rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={updateCredential}
              className="flex-1 bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
